import { useTranslation } from 'react-i18next';
import { Check, Circle, Minus, type LucideIcon } from 'lucide-react';
import type { Kolom, Lijst, Sortering } from '@xxtract/tabelfilters';
import { ariaSort, SorteerbareKop } from '@/components/tabel';
import { StatusBadge, Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@xxtract/ui';
import type { PhysicalItem, StepStatus } from '../processes.api';

/** Opmaakklassen, geen zichtbare tekst. */
const CEL = {
  leeg: 'bg-[var(--background-muted)]',
  hier: 'bg-[var(--secondary)]',
  stil: 'bg-[var(--destructive)]',
  klaar: 'bg-[var(--success-light)] border border-[var(--success-dark)]',
};

/**
 * spec-fysiek-stapstatus (afleiding §3) — per stapstatus vlak, rand, symbool en symboolkleur. Het
 * symbool gebruikt de `-text`-tokens: `--success-dark` en `--secondary` halen op hun lichte vlak geen 3:1.
 */
const STATUS: Record<StepStatus, { vlak: string; icoon: LucideIcon; kleur: string }> = {
  done: { vlak: 'bg-[var(--success-light)] border border-[var(--success-dark)]', icoon: Check, kleur: 'text-[var(--success-text)]' },
  open: { vlak: 'bg-[var(--secondary-light)] border border-[var(--secondary)]', icoon: Circle, kleur: 'text-[var(--secondary-text)]' },
  notApplicable: { vlak: 'bg-[var(--background-muted)] border border-dashed border-[var(--border)]', icoon: Minus, kleur: 'text-[var(--foreground-muted)]' },
};

/** De huidige cel met stapstatus: de vulling van vandaag plus een rand, symbool wit op teal of rood. */
const RAND = 'ring-2 ring-[var(--foreground)]';
const WIT = 'text-white';
const NIET_VAN_TOEPASSING: StepStatus = 'notApplicable';

/** De drie stapstatussen in de legenda, met hun legendasleutel. */
const LEGENDA_STATUS: { status: StepStatus; sleutel: string }[] = [
  { status: 'done', sleutel: 'stepDone' },
  { status: 'open', sleutel: 'stepOpen' },
  { status: 'notApplicable', sleutel: 'stepNotApplicable' },
];

/** De laatste sport: een product daar is klaar en krijgt de afgeronde kleur, geen alarm. */
const EIND_STAPPEN = new Set(['returned']);

const STAP_VERTALING = 'processes.physical.steps.';

/**
 * spec-keten-gelijke-kolommen — elke stapkolom even breed (6 rem, schaalt mee met de lettergrootte); de
 * kopnaam breekt af, zo nodig binnen een woord, in plaats van zijn kolom te verbreden.
 */
const STAP_KOP = 'w-24 min-w-24 text-center';
const STAP_NAAM = 'block text-[10px] leading-tight hyphens-auto [overflow-wrap:anywhere]';

/** De lijstkolommen die de tabel als kop toont, in beeldvolgorde; `sinds` filtert alleen (datumfilter). */
const KOPPEN = ['product', 'klant', 'stap', 'werkdagen', 'toestand'] as const;

/**
 * De beeldtaal van `mockup-fysiek-leveringen.html`, toegepast op producten in plaats van leveringen.
 * De mockup tekent een rij per levering; hier is het één rij per product op een lopende order, in de
 * stap waar het nu staat (spec-processen-actuele-stand). De kolommen komen data-gedreven uit `chain`
 * (ontvangst tot retour, geen aanmelding): per product zie je in één oogopslag waar in de keten hij
 * staat, en per kolom hoeveel er staan.
 *
 * De kolommen komen NAAST de bestaande kolommen, niet in plaats daarvan. Ze dragen de positie in
 * kleur, en kleur alleen is geen informatie: de stap en de stand staan als tekst in dezelfde rij.
 * Een product in een toestand buiten de keten (ONHOLD) heeft geen gevulde kolom, maar wel een naam.
 */
export function KetenKolommen({
  chain,
  stepTotals,
  items,
  stepLabelFor,
  lijst,
  sortering,
  onSorteer,
}: {
  chain: string[];
  stepTotals: Record<string, number>;
  items: PhysicalItem[];
  stepLabelFor: (item: PhysicalItem) => string;
  /** spec-tabelfilters story 8: de kolombeschrijving, de gestapelde sortering en een klik op een kop. */
  lijst: Lijst<PhysicalItem>;
  sortering: Sortering;
  onSorteer: (sleutel: string) => void;
}) {
  const { t } = useTranslation();
  const standaard = lijst.standaardSortering?.({});
  // spec-fysiek-stapstatus CAP-5: de legenda toont alleen de staten die de tabel kan tonen.
  const metStatus = items.some((item) => item.stepStatuses != null);
  const zonderStatus = items.length === 0 || items.some((item) => item.stepStatuses == null);
  const kolom = (sleutel: string) => lijst.kolommen.find((k) => k.sleutel === sleutel)! as Kolom<never, never>;

  return (
    <div className="overflow-x-auto">
      <Table data-testid="physical-items-table">
        {/* Het bijschrift noemt de standaardvolgorde; bij een eigen sortering zeggen de koppen het. */}
        {sortering.length === 0 ? <TableCaption>{t('processes.physicalItems.caption')}</TableCaption> : null}
        <TableHeader>
          <TableRow>
            {KOPPEN.map((sleutel) => (
              <TableHead aria-sort={ariaSort(sortering, sleutel)} key={sleutel} scope="col">
                <SorteerbareKop kolom={kolom(sleutel)} onKlik={() => onSorteer(sleutel)} sortering={sortering} standaard={standaard} />
              </TableHead>
            ))}
            {chain.map((stepKey) => (
              <TableHead className={STAP_KOP} data-testid={`keten-kop-${stepKey}`} key={stepKey} scope="col">
                <span className={STAP_NAAM}>{t(`${STAP_VERTALING}${stepKey}`, { defaultValue: stepKey })}</span>
                <span className="block text-xs font-bold text-[var(--foreground)]">{stepTotals[stepKey] ?? 0}</span>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow data-stagnating={item.stagnating} data-testid="physical-items-row" data-trade-item={item.tradeItemId} key={item.tradeItemId}>
              <TableCell className="font-medium">{item.gtin ?? item.tradeItemId}</TableCell>
              <TableCell>{item.gln ?? t('common.notAvailable')}</TableCell>
              <TableCell>{stepLabelFor(item)}</TableCell>
              <TableCell>
                {item.workdaysInStep == null ? t('common.notAvailable') : t('processes.physicalItems.workdays', { count: item.workdaysInStep })}
              </TableCell>
              <TableCell>
                {item.stagnating ? (
                  <StatusBadge announce={false} label={t('processes.physicalItems.status.stagnating')} status="process-on-hold" />
                ) : item.notReceived ? (
                  <StatusBadge announce={false} label={t('processes.physicalItems.status.notReceived')} status="process-future" />
                ) : (
                  <StatusBadge announce={false} label={t('processes.physicalItems.status.onTrack')} status="process-current" />
                )}
              </TableCell>
              {chain.map((stepKey) => {
                const hier = item.stepKey === stepKey;
                // Heeft de rij statussen, dan telt een ontbrekende stap als niet van toepassing.
                const status = item.stepStatuses == null ? undefined : (item.stepStatuses[stepKey] ?? NIET_VAN_TOEPASSING);
                const huidig = item.stagnating ? CEL.stil : EIND_STAPPEN.has(stepKey) ? CEL.klaar : CEL.hier;
                // Zonder status blijft de rij zoals vóór spec-fysiek-stapstatus.
                if (status == null) {
                  return (
                    <TableCell className="px-1" key={stepKey}>
                      {/* Een herhaling in beeld van de stap-kolom hiernaast; een schermlezer hoort die al. */}
                      <div aria-hidden="true" className={`h-6 rounded ${hier ? huidig : CEL.leeg}`} data-testid={hier ? `keten-cel-actief-${item.tradeItemId}` : undefined} />
                    </TableCell>
                  );
                }
                const { vlak, icoon: Icoon, kleur } = STATUS[status];
                // Wit op teal of rood, de afgeronde tint op de lichte retourvulling.
                const icoonKleur = !hier ? kleur : EIND_STAPPEN.has(stepKey) && !item.stagnating ? STATUS.done.kleur : WIT;
                const tekst = t('processes.physicalItems.stepStatus.cell', {
                  step: t(`${STAP_VERTALING}${stepKey}`, { defaultValue: stepKey }),
                  status: t(`processes.physicalItems.stepStatus.${status}`),
                });
                return (
                  <TableCell className="relative px-1" key={stepKey}>
                    <div className={`flex h-6 items-center justify-center rounded ${hier ? `${huidig} ${RAND}` : vlak}`} data-testid={hier ? `keten-cel-actief-${item.tradeItemId}` : undefined}>
                      <span className="flex h-full w-full items-center justify-center" data-status={status} data-testid={`keten-status-${item.tradeItemId}-${stepKey}`} title={tekst}>
                        <Icoon aria-hidden="true" className={`h-3.5 w-3.5 ${icoonKleur}`} data-icon={status} strokeWidth={3} />
                        <span className="sr-only">{tekst}</span>
                      </span>
                    </div>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ul className="mt-3 flex flex-wrap gap-4 text-[11px] text-[var(--foreground-muted)]" data-testid="keten-legenda">
        <li className="flex items-center gap-1.5"><span aria-hidden="true" className={`h-3 w-6 rounded ${CEL.hier} ${metStatus ? RAND : ''}`} />{t('processes.physicalItems.legend.here')}</li>
        <li className="flex items-center gap-1.5"><span aria-hidden="true" className={`h-3 w-6 rounded ${CEL.stil} ${metStatus ? RAND : ''}`} />{t('processes.physicalItems.legend.stagnating')}</li>
        <li className="flex items-center gap-1.5"><span aria-hidden="true" className={`h-3 w-6 rounded ${CEL.klaar} ${metStatus ? RAND : ''}`} />{t('processes.physicalItems.legend.done')}</li>
        {metStatus
          ? LEGENDA_STATUS.map(({ status, sleutel }) => {
              const { vlak, icoon: Icoon, kleur } = STATUS[status];
              return (
                <li className="flex items-center gap-1.5" key={status}>
                  <span aria-hidden="true" className={`flex h-4 w-6 items-center justify-center rounded ${vlak}`}>
                    <Icoon className={`h-3 w-3 ${kleur}`} strokeWidth={3} />
                  </span>
                  {t(`processes.physicalItems.legend.${sleutel}`)}
                </li>
              );
            })
          : null}
        {zonderStatus ? <li className="flex items-center gap-1.5"><span aria-hidden="true" className={`h-3 w-6 rounded ${CEL.leeg}`} />{t('processes.physicalItems.legend.empty')}</li> : null}
      </ul>
      {/* spec-fysiek-registratie-status CAP-6: wat "afgerond" bij Meten en Verpakking betekent. */}
      {metStatus ? <p className="mt-1 text-[11px] text-[var(--foreground-muted)]" data-testid="keten-legenda-toelichting">{t('processes.physicalItems.legend.stepDoneNote')}</p> : null}
    </div>
  );
}
